import { useEffect, useRef } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import { useCurrentTenant } from '@/shared/auth/store'

import { sessionKeys } from '../queries'
import { aiStore } from '../store'

/**
 * Resets the AIR® AI panel on tenant switch.
 *
 * On change:
 *  1. Aborts any in-flight chat stream (prevents a stale tenant's stream from
 *     finishing under the new tenant context).
 *  2. Clears `activeSessionId` so the panel falls back to its empty state —
 *     sessions are scoped per-tenant on the backend.
 *  3. Invalidates every cached `ai/sessions` query so the new tenant's list
 *     refetches the next time the panel opens.
 *
 * Side effects (Zustand `setState`, query invalidation) live inside `useEffect`
 * so they don't run during render. Other subscribers (Zustand listeners, query
 * observers) would otherwise be notified mid-render and React's concurrent
 * renderer could observe inconsistent snapshots. The `prevTenantRef` (mutated
 * inside the same effect) gates the side effect to exactly one run per
 * transition without falling into `set-state-in-effect`.
 *
 * Mount inside `<AIRailLayout>` so it runs whenever the panel is in the DOM.
 */
export function useTenantSwitchEffect(): void {
	const currentTenant = useCurrentTenant()
	const queryClient = useQueryClient()
	const prevTenantRef = useRef(currentTenant)

	useEffect(() => {
		if (prevTenantRef.current === currentTenant) return
		prevTenantRef.current = currentTenant
		aiStore.getState().actions.abortActiveStream()
		aiStore.setState({ activeSessionId: undefined }, false, 'ai/tenantSwitch')
		void queryClient.invalidateQueries({ queryKey: sessionKeys.all })
	}, [currentTenant, queryClient])
}
